import {matchesCountry, normalizeSearch} from "../../utils/countrySearch";
import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Airport, DestinationCountry } from "../../models/flightSearchData";
import { AirportResponse, AirportService } from "../../services/AirportService";
import { useTranslation } from "../../i18n/LanguageProvider";

interface AirportSelectorProps {
  compact?: boolean;
  selectedAirports: Airport[];
  selectedCountries: DestinationCountry[];
  setSelectedAirports: (airports: Airport[]) => void;
  setSelectedCountries: (countries: DestinationCountry[]) => void;
}

enum AirportTab {
  Airports = "airports",
  Countries = "countries",
}

const MAX_SELECTED_AIRPORTS = 3;

const AirportSelector: React.FC<AirportSelectorProps> = ({
  compact = false,
  selectedAirports,
  selectedCountries,
  setSelectedAirports,
  setSelectedCountries,
}) => {
  const [activeTab, setActiveTab] = useState<AirportTab>(AirportTab.Airports);
  const [query, setQuery] = useState("");
  const [airports, setAirports] = useState<AirportResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    let isMounted = true;

    const loadAirports = async () => {
      setIsLoading(true);
      setLoadError(false);
      try {
        const result = await AirportService.getAirports();
        if (isMounted) {
          setAirports(result);
        }
      } catch (error) {
        console.error(t("airport.error"), error);
        if (isMounted) {
          setLoadError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadAirports();

    return () => {
      isMounted = false;
    };
  }, [t]);

  const countries = useMemo(() => {
    const byCode = new Map<string, DestinationCountry>();
    airports.forEach((airport) => {
      byCode.set(airport.country_code, {
        code: airport.country_code,
        name: airport.country,
      });
    });

    return Array.from(byCode.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [airports]);

  const normalizedQuery = normalizeSearch(query);
  const selectedCountryCode = selectedCountries[0]?.code;

  const filteredAirports = useMemo(() => {
    return airports
      .filter((airport) => {
        if (selectedCountryCode && airport.country_code !== selectedCountryCode) {
          return false;
        }

        if (!normalizedQuery) return true;
          return (
          normalizeSearch(airport.city).includes(normalizedQuery) ||
          normalizeSearch(airport.name).includes(normalizedQuery) ||
          normalizeSearch(airport.iata).includes(normalizedQuery) ||
          matchesCountry(airport.country_code, normalizedQuery, airport.country)
        );
      })
      .slice(0, 80);
  }, [airports, normalizedQuery, selectedCountryCode]);

  const filteredCountries = useMemo(() => {
    return countries.filter((country) => {
      if (!normalizedQuery) return true;
      return (
        matchesCountry(country.code, normalizedQuery, country.name)
      );
    });
  }, [countries, normalizedQuery]);

  const clearSelection = () => {
    setSelectedAirports([]);
    setSelectedCountries([]);
  };

  const selectCountry = (country: DestinationCountry) => {
    setSelectedCountries([country]);
    setSelectedAirports(
      selectedAirports.filter((airport) => {
        const fullAirport = airports.find((item) => item.iata === airport.code);
        return fullAirport?.country_code === country.code;
      })
    );
    setActiveTab(AirportTab.Airports);
    setQuery("");
  };

  const toggleAirport = (airport: AirportResponse) => {
    const selected = selectedAirports.some((item) => item.code === airport.iata);
    const country = { code: airport.country_code, name: airport.country };

    if (selected) {
      const nextAirports = selectedAirports.filter((item) => item.code !== airport.iata);
      setSelectedAirports(nextAirports);
      if (nextAirports.length === 0) {
        setSelectedCountries([]);
      }
      return;
    }

    if (selectedCountryCode && selectedCountryCode !== airport.country_code) {
      setSelectedCountries([country]);
      setSelectedAirports([{ city: airport.city, code: airport.iata }]);
      return;
    }

    if (selectedAirports.length >= MAX_SELECTED_AIRPORTS) {
      return;
    }

    setSelectedCountries([country]);
    setSelectedAirports([...selectedAirports, { city: airport.city, code: airport.iata }]);
  };

  const searchControls = (<>
      <div className="flex flex-wrap gap-3 justify-between items-start mb-4">
        <h2 className="selector-heading text-accent font-semibold text-sm">{t("airport.title")}</h2>
        {(selectedAirports.length > 0 || selectedCountries.length > 0) && (
          <button onClick={clearSelection} className="text-sm text-[#A0A0A0] hover:underline">
            {t("airport.clear")}
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab(AirportTab.Airports)}
          className={classNames(
            "px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 ease-in-out",
            activeTab === AirportTab.Airports
              ? "bg-[#4B4B4B] text-white"
              : "bg-[#EAEAEA] text-[#4B4B4B] hover:bg-[#D5D5D5]"
          )}
        >
          {t("common.airports")}
        </button>
        <button
          onClick={() => setActiveTab(AirportTab.Countries)}
          className={classNames(
            "px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 ease-in-out",
            activeTab === AirportTab.Countries
              ? "bg-[#4B4B4B] text-white"
              : "bg-[#EAEAEA] text-[#4B4B4B] hover:bg-[#D5D5D5]"
          )}
        >
          {t("common.countries")}
        </button>
      </div>

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={activeTab === AirportTab.Airports ? t("common.searchAirport") : t("common.searchCountry")}
        className="w-full max-w-2xl rounded-xl border border-[#D8D8D8] bg-white px-4 py-3 mb-4 text-sm text-[#4B4B4B] outline-none transition-smooth focus:border-[#7745F5]"
      />

  </>);

  return (
    <div className="selector-panel airport-panel p-4 sm:p-6 bg-[#FBFBFF] shadow-card rounded-2xl w-full transition-smooth">
      {!compact && searchControls}

      {selectedCountries.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 max-w-2xl transition-smooth">
          <span className="rounded-xl bg-[#F3F1FF] px-3 py-1.5 text-sm font-bold text-accent">
            {selectedCountries[0].code} {selectedCountries[0].name}
          </span>
          <span className="text-xs font-bold text-[#A0A0A0]">
            {t("airport.count", { count: selectedAirports.length, max: MAX_SELECTED_AIRPORTS })}
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-[#A0A0A0]">{t("airport.loading")}</div>
      ) : loadError ? (
        <div className="text-sm text-[#A0A0A0]">{t("airport.error")}</div>
      ) : (
        <div className="flex flex-wrap gap-3 max-w-2xl max-h-72 overflow-y-auto pr-1 transition-smooth">
          {activeTab === AirportTab.Airports && filteredAirports.map((airport) => {
              const selected = selectedAirports.some((item) => item.code === airport.iata);
              const disabled =
                !selected &&
                selectedAirports.length >= MAX_SELECTED_AIRPORTS &&
                selectedCountryCode === airport.country_code;

              return (
                <button
                  key={airport.iata}
                  aria-pressed={selected}
                  onClick={() => toggleAirport(airport)}
                  disabled={disabled}
                  className={classNames(
                    "rounded-xl px-4 py-2 font-bold text-sm transition-all duration-300 ease-in-out flex items-center gap-2",
                    selected
                      ? "bg-[#4B4B4B] hover:bg-[#3F3F3F]"
                      : disabled
                        ? "bg-[#F0F0F0] text-[#BBBBBB] cursor-not-allowed"
                        : "bg-[#EAEAEA] hover:bg-[#D5D5D5] hover:scale-105"
                  )}
                >
                  <span className={selected ? "text-white" : "text-[#535353]"}>{airport.city}</span>
                  <span className={selected ? "text-[#D7D7D7]" : "text-[#9C9C9C]"}>{airport.iata}</span>
                </button>
              );
            })}

          {activeTab === AirportTab.Countries && filteredCountries.map((country) => {
              const selected = selectedCountries.some((item) => item.code === country.code);
              return (
                <button
                  key={country.code}
                  aria-pressed={selected}
                  onClick={() => selectCountry(country)}
                  className={classNames(
                    "rounded-xl px-4 py-2 hover:scale-105 font-bold text-sm transition-all duration-300 ease-in-out flex items-center gap-2",
                    selected
                      ? "bg-[#4B4B4B] hover:bg-[#3F3F3F]"
                      : "bg-[#EAEAEA] hover:bg-[#D5D5D5]"
                  )}
                >
                  <span className={selected ? "text-[#D7D7D7]" : "text-[#9C9C9C]"}>{country.code}</span>
                  <span className={selected ? "text-white" : "text-[#535353]"}>{country.name}</span>
                </button>
              );
            })}

          {activeTab === AirportTab.Airports && filteredAirports.length === 0 && (
            <div className="text-sm text-[#A0A0A0]">{t("airport.emptyAirports")}</div>
          )}

          {activeTab === AirportTab.Countries && filteredCountries.length === 0 && (
            <div className="text-sm text-[#A0A0A0]">{t("airport.emptyCountries")}</div>
          )}
        </div>
      )}
      {compact && <details className="mobile-airport-search">
        <summary>{t("common.searchAirport")}</summary>
        {searchControls}
      </details>}
    </div>
  );
};

export default AirportSelector;
